import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  FILTER_CATEGORY_LABELS,
  filterOptionKey,
  groupFilterOptions,
  type ActiveDecisionFilter,
  type DecisionFilterCategory,
  type DecisionFilterOption,
} from './decisionFilters';

interface Props {
  options: DecisionFilterOption[];
  active: ActiveDecisionFilter[];
  onChange: (filters: ActiveDecisionFilter[]) => void;
  /** Raise above fullscreen overlays when used inside pro mode. */
  popoverZIndex?: number;
}

const CATEGORY_ORDER: DecisionFilterCategory[] = [
  'judgement',
  'on_field',
  'on_field_punishment',
  'reviewed',
  'reviewed_punishment',
];

function FilterCategorySection({
  category,
  items,
  activeKeys,
  onToggle,
}: {
  category: DecisionFilterCategory;
  items: DecisionFilterOption[];
  activeKeys: Set<string>;
  onToggle: (item: DecisionFilterOption) => void;
}) {
  const total = items.reduce((s, i) => s + i.count, 0);
  if (items.length === 0) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {FILTER_CATEGORY_LABELS[category]} ({total})
      </Typography>
      <Stack spacing={0}>
        {items.map((item) => {
          const key = filterOptionKey(item);
          const checked = activeKeys.has(key);
          return (
            <FormControlLabel
              key={key}
              sx={{ mx: 0, py: 0.15, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() => onToggle(item)}
                  sx={{ py: 0.5 }}
                />
              }
              label={
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 0.5 }}>
                  <Typography variant="body2">
                    {item.label} ({item.count})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.percent} %
                  </Typography>
                </Stack>
              }
            />
          );
        })}
      </Stack>
    </Box>
  );
}

export default function DecisionFilterPanel({ options, active, onChange, popoverZIndex }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const grouped = groupFilterOptions(options);
  const hasAny = CATEGORY_ORDER.some((c) => grouped[c].length > 0);

  const activeKeys = useMemo(() => new Set(active.map(filterOptionKey)), [active]);

  if (!hasAny) return null;

  const toggle = (item: DecisionFilterOption) => {
    const key = filterOptionKey(item);
    if (activeKeys.has(key)) {
      onChange(active.filter((f) => filterOptionKey(f) !== key));
    } else {
      onChange([...active, { category: item.category, value: item.value }]);
    }
  };

  return (
    <>
      <Tooltip title="判決篩選">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          color={active.length > 0 ? 'secondary' : 'default'}
          aria-label="判決篩選"
        >
          <Badge badgeContent={active.length || undefined} color="secondary">
            <FilterListIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          root: popoverZIndex != null ? { sx: { zIndex: popoverZIndex } } : undefined,
          paper: { sx: { width: 300, maxHeight: 420 } },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={800}>
              判決篩選
            </Typography>
            {active.length > 0 && (
              <Button size="small" onClick={() => onChange([])}>
                清除
              </Button>
            )}
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
            {CATEGORY_ORDER.map((cat, idx) => (
              <Box key={cat}>
                {idx > 0 && grouped[cat].length > 0 && <Divider sx={{ my: 1 }} />}
                <FilterCategorySection
                  category={cat}
                  items={grouped[cat]}
                  activeKeys={activeKeys}
                  onToggle={toggle}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
